import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  FilterBar,
  ConfirmDialog,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Select,
} from '../../../design-system/components';
import { formatDate } from '../../../lib/formatters';
import {
  admin_affiliates_path,
  admin_affiliate_path,
  bulk_approve_admin_affiliates_path,
  bulk_deny_admin_affiliates_path,
  bulk_deactivate_admin_affiliates_path,
  bulk_reassign_admin_affiliates_path,
} from '../../../lib/routes';
const statusVariant = {
  approved: 'success',
  pending: 'warning',
  denied: 'danger',
  deactivated: 'default',
};
const columns = [
  {
    id: 'name',
    header: 'Name',
    render: (row) => (
      <Link
        href={admin_affiliate_path(row.id)}
        className="font-medium text-fg hover:text-fg-secondary"
      >
        {row.name}
      </Link>
    ),
  },
  {
    id: 'email',
    header: 'Email',
    accessor: 'email',
  },
  {
    id: 'slug',
    header: 'Slug',
    render: (row) => (
      <span className="font-mono text-xs text-fg-tertiary">{row.slug || '\u2014'}</span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={statusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'created_at',
    header: 'Applied',
    render: (row) => <span className="text-sm text-fg-tertiary">{formatDate(row.created_at)}</span>,
  },
];
export default function Index({ affiliates, campaigns, filters }) {
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [reassignCampaignId, setReassignCampaignId] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const selectedCount = selectedRows.size;
  const filterDefs = [
    {
      param: 'status',
      label: 'Status',
      value: filters.status || '',
      options: [
        {
          label: 'Pending',
          value: 'pending',
        },
        {
          label: 'Approved',
          value: 'approved',
        },
        {
          label: 'Denied',
          value: 'denied',
        },
        {
          label: 'Deactivated',
          value: 'deactivated',
        },
      ],
    },
    {
      param: 'campaign_id',
      label: 'Campaign',
      value: filters.campaign_id || '',
      options: campaigns.map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    },
  ];
  function handleBulkAction(action) {
    if (selectedCount === 0) return;
    setBulkAction(action);
  }
  function executeBulkAction() {
    if (!bulkAction || selectedCount === 0) return;
    setBulkLoading(true);
    const affiliateIds = Array.from(selectedRows);
    const data = {
      affiliate_ids: affiliateIds,
    };
    let url;
    switch (bulkAction) {
      case 'approve':
        url = bulk_approve_admin_affiliates_path();
        break;
      case 'deny':
        url = bulk_deny_admin_affiliates_path();
        break;
      case 'deactivate':
        url = bulk_deactivate_admin_affiliates_path();
        break;
      case 'reassign':
        url = bulk_reassign_admin_affiliates_path();
        data.campaign_id = reassignCampaignId;
        break;
      default:
        return;
    }
    router.post(url, data, {
      onFinish: () => {
        setBulkLoading(false);
        setBulkAction(null);
        setSelectedRows(new Set());
        setReassignCampaignId('');
      },
    });
  }
  const bulkMessages = {
    approve: {
      title: 'Approve Affiliates',
      message: `Are you sure you want to approve ${selectedCount} affiliate${selectedCount !== 1 ? 's' : ''}?`,
      destructive: false,
    },
    deny: {
      title: 'Deny Affiliates',
      message: `Are you sure you want to deny ${selectedCount} affiliate${selectedCount !== 1 ? 's' : ''}?`,
      destructive: true,
    },
    deactivate: {
      title: 'Deactivate Affiliates',
      message: `Are you sure you want to deactivate ${selectedCount} affiliate${selectedCount !== 1 ? 's' : ''}? This will reverse any pending commissions.`,
      destructive: true,
    },
  };
  const bulkActionBar = (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => handleBulkAction('approve')}>
        Approve
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleBulkAction('deny')}>
        Deny
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleBulkAction('deactivate')}>
        Deactivate
      </Button>
      <Button size="sm" variant="outline" onClick={() => handleBulkAction('reassign')}>
        Reassign
      </Button>
    </div>
  );
  return (
    <AdminLayout>
      <PageHeader title="Affiliates" description="Manage your affiliate partners" />

      <div className="mt-6 space-y-4">
        <FilterBar
          url={admin_affiliates_path()}
          filters={filterDefs}
          search={{
            paramName: 'search',
            value: filters.search || '',
            placeholder: 'Search by name, email, or slug...',
          }}
        />

        <DataTable
          columns={columns}
          data={affiliates}
          keyAccessor="id"
          selectable
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          bulkActions={bulkActionBar}
          emptyTitle="No affiliates found"
          emptyDescription="Affiliates will appear here once they apply to your program."
        />
      </div>

      {/* Bulk approve/deny/deactivate confirm */}
      {bulkAction && bulkAction !== 'reassign' && bulkMessages[bulkAction] && (
        <ConfirmDialog
          open
          onClose={() => setBulkAction(null)}
          onConfirm={executeBulkAction}
          title={bulkMessages[bulkAction].title}
          message={bulkMessages[bulkAction].message}
          confirmLabel={bulkMessages[bulkAction].title}
          destructive={bulkMessages[bulkAction].destructive}
          loading={bulkLoading}
        />
      )}

      {/* Bulk reassign modal */}
      {bulkAction === 'reassign' && (
        <Modal
          open
          onClose={() => {
            setBulkAction(null);
            setReassignCampaignId('');
          }}
          size="sm"
        >
          <ModalTitle>Reassign Campaign</ModalTitle>
          <ModalBody>
            <p className="mb-3 text-sm text-fg-secondary">
              Select a campaign to reassign {selectedCount} affiliate
              {selectedCount !== 1 ? 's' : ''} to:
            </p>
            <Select
              label="Campaign"
              value={reassignCampaignId}
              onChange={(e) => setReassignCampaignId(e.target.value)}
            >
              <option value="">Select a campaign...</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkAction(null);
                setReassignCampaignId('');
              }}
              disabled={bulkLoading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={executeBulkAction}
              loading={bulkLoading}
              disabled={!reassignCampaignId}
            >
              Reassign
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </AdminLayout>
  );
}
